import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PAYMENT_GROUPS,
  PaymentGroup,
  PaymentGroupEnum,
  PaymentRowKindEnum,
  TPaymentListItem
} from '../types/ManagePayments';
import { ScheduledBankPaymentGroupStateEnum } from '@/features/payments/types/BatchPayment';
import useAuth from '@/hooks/useAuth';
import { AccountTypeEnum } from '@/context/types/Account';
import { canAccessBatchPayments } from '@/utils/canAccessBatchPayments';
import useHomeContext from '@/hooks/useHomeContext';
import { NotificationType } from '@/context/types/Notification';
import {
  applyPaymentUpdateToGroups,
  removePaymentFromGroups,
  TPaymentUpdate
} from '../utils/applyPaymentUpdateToGroups';
import {
  EMPTY_PAYMENTS_BY_GROUP,
  fetchPaymentsByGroup,
  GROUP_BY_STATE
} from '../utils/fetchPaymentsByGroup';
import { useInProgressGroupPolling } from './useInProgressGroupPolling';
import { isBatchIncomplete } from '../data/paymentStatus';

/**
 * Named distinctly from features/payments/hooks/useBatchPayment.ts (the batch-creation
 * submission/polling hook) - the two share no code and live in sibling-ish feature folders, so
 * an identical name was a recurring source of confusion when navigating between them.
 */
export const useManagePayment = () => {
  const { accountId, business, accountType } = useAuth()!;
  const isBusinessAccount = accountType === AccountTypeEnum.BUSINESS;
  const hasBatchPaymentAccess = canAccessBatchPayments(accountType, business?.accessLevel);
  const { wallets, setIsLoading: setGlobalLoading, triggerNotification } = useHomeContext()!;
  const [isLoading, setIsLoading] = useState(true);
  const [paymentsByGroup, setPaymentsByGroup] =
    useState<Record<PaymentGroup, TPaymentListItem[]>>(EMPTY_PAYMENTS_BY_GROUP);

  // Lets a caller (e.g. "Review failed payments" on the batch result screen) deep-link straight
  // into a specific tab via ?tab=<group>, e.g. /home/manage/payment?tab=action_required.
  // Defaults to Scheduled when the param is missing, isn't one of the known groups, or requests
  // Batch history for a profile without batch payment access (that tab is hidden from them - see
  // PaymentsTabBar - so there's nothing there to land on).
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const isValidRequestedTab =
    PAYMENT_GROUPS.includes(requestedTab as PaymentGroup) &&
    (requestedTab !== PaymentGroupEnum.BATCH_HISTORY || hasBatchPaymentAccess);
  const initialTab = isValidRequestedTab
    ? (requestedTab as PaymentGroup)
    : PaymentGroupEnum.SCHEDULED;
  const [activeTab, setActiveTab] = useState<PaymentGroup>(initialTab);

  useEffect(() => {
    if (!accountId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // Fetch-and-bucket pipeline shared with the nav's approval-required count badge (see
    // fetchPaymentsByGroup) - three independently-sourced lists merged before bucketing: batch
    // groups, individual scheduled payments (never belonged to a group), and individual payments
    // still awaiting multi-sign approval (sourced from the multi-sign action itself - see
    // getPendingScheduledPaymentActions).
    fetchPaymentsByGroup({
      accountId,
      isBusinessAccount,
      businessId: business?.id,
      hasBatchPaymentAccess
    })
      .then(setPaymentsByGroup)
      .catch(() => setPaymentsByGroup(EMPTY_PAYMENTS_BY_GROUP))
      .finally(() => {
        setIsLoading(false);
        setGlobalLoading(false);
      });
  }, [accountId, isBusinessAccount, business?.id, hasBatchPaymentAccess]);

  /**
   * Applies a known state change to one row without refetching the whole list - the modal
   * already knows the outcome of its own approve/reject call, so there's no need to pay for a
   * network round trip just to move one item between tabs. Moves the row to whichever tab
   * `GROUP_BY_STATE` maps its new state to; if that's the same tab, it's patched in place.
   */
  const applyPaymentUpdate = useCallback((patch: TPaymentUpdate) => {
    setPaymentsByGroup((prev) => applyPaymentUpdateToGroups(prev, patch, GROUP_BY_STATE));
  }, []);

  // Same no-refetch reasoning as applyPaymentUpdate, for the case where the row should
  // disappear entirely (a successful delete) rather than move to a different tab.
  const removePayment = useCallback((id: string) => {
    setPaymentsByGroup((prev) => removePaymentFromGroups(prev, id));
  }, []);

  // IN_PROGRESS only ever appears on a real batch group (individual scheduled payments only ever
  // map to APPROVED/FAILED/PENDING_APPROVAL - see services/scheduledPayment.ts and
  // multiSign.ts), so this is exhaustive without needing to check every tab.
  const inProgressGroupIds = paymentsByGroup[PaymentGroupEnum.SCHEDULED]
    .filter(
      (item) =>
        item.kind === PaymentRowKindEnum.BATCH &&
        item.state === ScheduledBankPaymentGroupStateEnum.IN_PROGRESS
    )
    .map((item) => item.id);

  // Surfaces a toast the moment a poll observes a batch leaving IN_PROGRESS, on top of the row
  // move applyPaymentUpdate already does. useInProgressGroupPolling stops that batch's own timer
  // as soon as this fires (and every timer on unmount/navigation), so this only ever runs once
  // per batch.
  const handleInProgressUpdate = useCallback(
    (patch: TPaymentUpdate) => {
      if (patch.state && patch.state !== ScheduledBankPaymentGroupStateEnum.IN_PROGRESS) {
        const existing = PAYMENT_GROUPS.flatMap((group) => paymentsByGroup[group]).find(
          (item) => item.id === patch.id
        );
        if (existing) {
          if (patch.state === ScheduledBankPaymentGroupStateEnum.COMPLETED) {
            triggerNotification(NotificationType.BATCH_PAYMENT_COMPLETE, { name: existing.name });
          } else if (isBatchIncomplete(patch.state)) {
            triggerNotification(NotificationType.BATCH_PAYMENT_INCOMPLETE, {
              name: existing.name
            });
          }
        }
      }
      applyPaymentUpdate(patch);
    },
    [paymentsByGroup, applyPaymentUpdate, triggerNotification]
  );
  useInProgressGroupPolling(inProgressGroupIds, handleInProgressUpdate);

  const counts: Record<PaymentGroup, number> = {
    [PaymentGroupEnum.SCHEDULED]: paymentsByGroup[PaymentGroupEnum.SCHEDULED].length,
    [PaymentGroupEnum.ACTION_REQUIRED]: paymentsByGroup[PaymentGroupEnum.ACTION_REQUIRED].length,
    [PaymentGroupEnum.BATCH_HISTORY]: paymentsByGroup[PaymentGroupEnum.BATCH_HISTORY].length
  };

  // Row sorting is owned by the DataTable (client mode); here we only enrich with wallet details.
  const rowsByGroup = useMemo(
    () =>
      PAYMENT_GROUPS.reduce(
        (acc, group) => {
          acc[group] = paymentsByGroup[group].map((item) => {
            const isTransfer = item.kind === PaymentRowKindEnum.SCHEDULED_TRANSFER;
            return {
              ...item,
              payFromAccount: wallets.find((wallet) => wallet.id === item.walletId) ?? null,
              // Only a scheduled transfer needs this - the backend only returns `toWalletId` (see
              // services/scheduledWalletTransfer.ts), so the To/Name column's display name is
              // resolved here, same deferred-lookup reasoning as payFromAccount above. Every other
              // kind already carries its real `name` (payee/batch name) straight from its own
              // transform.
              name: isTransfer
                ? wallets.find((wallet) => wallet.id === item.toWalletId)?.name ?? item.name
                : item.name
            };
          });
          return acc;
        },
        {} as Record<PaymentGroup, TPaymentListItem[]>
      ),
    [paymentsByGroup, wallets]
  );

  return {
    rowsByGroup,
    counts,
    activeTab,
    setActiveTab,
    isLoading,
    applyPaymentUpdate,
    removePayment,
    hasBatchPaymentAccess
  };
};
